import { useCallback, useEffect, useState } from 'react';
import { ApiError, request } from '@/lib/api.js';
import { useToast } from '@/components/Toast.js';
import {
  GlassButton,
  LoadingState,
  PageHeader,
  useConfirm,
} from '@/components/ui/index.js';
import { useFmt } from '@/lib/useFmt.js';
import type { PricingPlan, PricingResponse } from '@/lib/types.js';

/**
 * Admin pricing page — flip prices (CNY / USD) and Stripe price IDs live,
 * no redeploy. Changes go to `app_settings` table; marketing pages pull
 * from /pricing on every render, Stripe checkout reads on every session
 * create, so flipping the row is enough.
 *
 * Critical workflow for the founder's payment test:
 *   1. In Stripe Dashboard, create a $1 test price (or use an existing
 *      cheap one) under the same product as the live $9.99.
 *   2. Here, paste that test price_id into "monthly · stripe_price_id"
 *      and Save.
 *   3. Go to /app/buy, click Subscribe → Stripe checkout shows $1.
 *   4. Pay with your real card; verify in Stripe Dashboard money arrives.
 *   5. Come back here, paste the original $9.99 price_id, Save.
 *   6. Refund the $1 from Stripe Dashboard.
 *
 * Total cost ≈ $0.30 Stripe fee. All changes audit-logged with diff.
 */

const STRIPE_PRICE_ID_RE = /^price_[A-Za-z0-9]+$/;

interface DraftPlan {
  cny: string;
  usd: string;
  stripe_price_id: string;
}

const emptyDraft: DraftPlan = { cny: '', usd: '', stripe_price_id: '' };

function planToDraft(plan: PricingPlan | null): DraftPlan {
  if (plan === null) return { ...emptyDraft };
  return {
    cny: String(plan.cny),
    usd: String(plan.usd),
    stripe_price_id: plan.stripe_price_id,
  };
}

function draftToPlan(draft: DraftPlan): PricingPlan | { error: string } {
  const cny = Number(draft.cny);
  const usd = Number(draft.usd);
  if (!Number.isFinite(cny) || cny <= 0) return { error: 'CNY 必须是正数' };
  if (!Number.isFinite(usd) || usd <= 0) return { error: 'USD 必须是正数' };
  if (!STRIPE_PRICE_ID_RE.test(draft.stripe_price_id)) {
    return { error: 'Stripe Price ID 必须以 price_ 开头' };
  }
  return { cny, usd, stripe_price_id: draft.stripe_price_id };
}

export function PricingPage(): JSX.Element {
  const [data, setData] = useState<PricingResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [monthlyDraft, setMonthlyDraft] = useState<DraftPlan>(emptyDraft);
  const [yearlyDraft, setYearlyDraft] = useState<DraftPlan>(emptyDraft);
  const { fmtDateTime } = useFmt();
  const confirm = useConfirm();

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await request<PricingResponse>('/admin/pricing');
      setData(res);
      setMonthlyDraft(planToDraft(res.monthly));
      setYearlyDraft(planToDraft(res.yearly));
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(): Promise<void> {
    setError(null);
    const m = draftToPlan(monthlyDraft);
    const y = draftToPlan(yearlyDraft);
    if ('error' in m) {
      setError(`月卡: ${m.error}`);
      return;
    }
    if ('error' in y) {
      setError(`年卡: ${y.error}`);
      return;
    }
    // 改价对所有真实结账立即生效(marketing 60s 内拉到、Stripe checkout 立即用新
    // price_id)。这是全后台唯一直接改钱的写操作, 加确认回显让 admin 核对(防少打一位)。
    const ok = await confirm({
      title: '更新线上价格',
      message: `即将对所有真实结账立即生效。月卡 ¥${m.cny}(${m.stripe_price_id})、年卡 ¥${y.cny}(${y.stripe_price_id})。请核对价格与 price_id 无误。`,
      confirmLabel: '确认更新',
      tone: 'danger',
    });
    if (!ok) return;
    setSaving(true);
    try {
      await request('/admin/pricing', {
        method: 'PUT',
        body: { monthly: m, yearly: y },
      });
      useToast.getState().show('success', '价格已更新');
      await load();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : '保存失败';
      setError(msg);
      useToast.getState().show('error', msg);
    } finally {
      setSaving(false);
    }
  }

  if (loading && data === null) return <LoadingState />;

  const dirty =
    JSON.stringify(monthlyDraft) !== JSON.stringify(planToDraft(data?.monthly ?? null)) ||
    JSON.stringify(yearlyDraft) !== JSON.stringify(planToDraft(data?.yearly ?? null));

  return (
    <div className="space-y-6">
      <PageHeader
        title="定价"
        subtitle="改完立即生效。marketing 页 60s 内拉到新价格，Stripe checkout 立即用新 price_id"
      />

      {error !== null && (
        <div className="rounded-lg bg-rose-900/30 border border-rose-700/50 px-3 py-2 text-sm text-rose-300">
          {error}
        </div>
      )}

      {/* Workflow tip for payment testing */}
      <div className="rounded-xl border border-amber-700/40 bg-amber-900/20 px-4 py-3 text-sm text-amber-100/90">
        <p className="font-semibold mb-1">💡 付费测试流程</p>
        <ol className="text-xs text-amber-100/80 space-y-0.5 list-decimal pl-5">
          <li>在 Stripe Dashboard 建一个 $1 测试 price（同 product 下）</li>
          <li>把 $1 的 price_id 贴到下面"月卡 · Stripe Price ID"保存</li>
          <li>去 littleshark.app/app/buy 真实付款 → 验证钱到 Stripe 账户</li>
          <li>回来把原 price_id 贴回保存</li>
          <li>Stripe Dashboard 退款 $1，净成本约 $0.30 手续费</li>
        </ol>
      </div>

      <PlanEditor
        label="月卡 (Monthly)"
        draft={monthlyDraft}
        onChange={setMonthlyDraft}
        meta={data?.meta.monthly ?? null}
        fmtDateTime={fmtDateTime}
      />
      <PlanEditor
        label="年卡 (Yearly)"
        draft={yearlyDraft}
        onChange={setYearlyDraft}
        meta={data?.meta.yearly ?? null}
        fmtDateTime={fmtDateTime}
      />

      <div className="flex items-center justify-end gap-3 sticky bottom-0 bg-slate-950/80 backdrop-blur py-4 -mx-4 px-4 border-t border-white/[0.08]">
        {dirty && <span className="text-xs text-amber-300">未保存的修改</span>}
        <GlassButton variant="ghost" onClick={(): void => void load()} disabled={saving}>
          重置
        </GlassButton>
        <GlassButton
          variant="primary"
          onClick={(): void => void save()}
          disabled={saving || !dirty}
        >
          {saving ? '保存中…' : '保存'}
        </GlassButton>
      </div>
    </div>
  );
}

interface PlanEditorProps {
  label: string;
  draft: DraftPlan;
  onChange: (next: DraftPlan) => void;
  meta: { updated_at: string; updated_by_username: string | null } | null;
  fmtDateTime: (s: string | null | undefined) => string;
}

function PlanEditor({
  label,
  draft,
  onChange,
  meta,
  fmtDateTime,
}: PlanEditorProps): JSX.Element {
  return (
    <section className="rounded-xl border border-white/10 bg-white/[0.04] p-5 space-y-4 backdrop-blur">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold text-white tracking-tight">{label}</h2>
        {meta !== null && (
          <p className="text-xs text-slate-500">
            上次修改 · {fmtDateTime(meta.updated_at)}
            {meta.updated_by_username !== null && (
              <span className="ml-2">by {meta.updated_by_username}</span>
            )}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field
          label="CNY 价格（闲鱼 / marketing 显示）"
          prefix="¥"
          value={draft.cny}
          onChange={(v): void => onChange({ ...draft, cny: v })}
          placeholder="79"
        />
        <Field
          label="USD 价格（海外 Stripe / marketing 显示）"
          prefix="$"
          value={draft.usd}
          onChange={(v): void => onChange({ ...draft, usd: v })}
          placeholder="9.99"
        />
      </div>

      <Field
        label="Stripe Price ID（真实收费用）"
        value={draft.stripe_price_id}
        onChange={(v): void => onChange({ ...draft, stripe_price_id: v })}
        placeholder="price_1TZ..."
        mono
      />
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  prefix,
  mono = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  prefix?: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div>
      <label className="block text-xs text-slate-400 mb-1.5">{label}</label>
      <div className="flex items-center gap-2">
        {prefix !== undefined && (
          <span className="text-slate-500 text-sm w-4 shrink-0">{prefix}</span>
        )}
        <input
          type="text"
          value={value}
          onChange={(e): void => onChange(e.target.value)}
          placeholder={placeholder}
          className={`flex-1 h-9 px-3 bg-white/[0.03] border border-white/10 rounded-lg text-sm text-slate-100 focus:outline-none focus:border-shark-500 ${
            mono ? 'font-mono text-xs' : ''
          }`}
        />
      </div>
    </div>
  );
}
